import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import {
  CheckCircleOutlineOutlined, MenuBookOutlined, TrendingUpOutlined, ExploreOutlined, AddOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiService from "../../../service/Api";
import StatsCard from "../../bookings/list-booking2/StatsCard";
import CourseCard from "./CourseCard";
import type { AcademyCourse } from "./types";
import { ListPanel, PrimaryButton } from "./AcademyUi";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

interface DashboardResponse {
  courses: AcademyCourse[];
  in_progress: AcademyCourse[];
  completed: AcademyCourse[];
}

function CourseSection({ title, subtitle, courses, empty }: {
  title: string; subtitle?: string; courses: AcademyCourse[]; empty?: string;
}) {
  return (
    <ListPanel sx={{ mb: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
      </Box>
      {courses.length ? (
        <Grid container spacing={2.5}>
          {courses.map((course) => (
            <Grid key={course.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}><CourseCard course={course} /></Grid>
          ))}
        </Grid>
      ) : (
        <Box textAlign="center" py={4}><Typography sx={{ color: "text.secondary" }}>{empty}</Typography></Box>
      )}
    </ListPanel>
  );
}

export default function AcademyDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.ACADEMY_MANAGE);
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    apiService.get<DashboardResponse>("/api/lms/courses/dashboard/")
      .then((response) => setData(response.data))
      .catch(() => setError("Could not load your academy dashboard."));
  }, []);

  if (error) return <ListPanel><Box textAlign="center" py={4}><Typography color="error">{error}</Typography></Box></ListPanel>;
  if (!data) return <Box display="flex" justifyContent="center" py={8}><CircularProgress sx={{ color: "#4285F4" }} /></Box>;

  const suggested = data.courses.filter((course) => !course.enrolled && !course.completed).slice(0, 4);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 22 }}>Muzukuru Academy</Typography>
          <Typography variant="body2" color="text.secondary">Internal training for your role and team.</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <PrimaryButton startIcon={<ExploreOutlined />} onClick={() => navigate("/academy/courses")}>Browse Courses</PrimaryButton>
          {canManage && (
            <PrimaryButton startIcon={<AddOutlined />} onClick={() => navigate("/academy/admin/courses/create")}>Add Course</PrimaryButton>
          )}
        </Box>
      </Box>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <StatsCard title="Available Courses" value={data.courses.length} icon={<MenuBookOutlined />} backgroundColor="#4285F4" />
          <StatsCard title="In Progress" value={data.in_progress.length} icon={<TrendingUpOutlined />} backgroundColor="#F4A742" />
          <StatsCard title="Completed" value={data.completed.length} icon={<CheckCircleOutlineOutlined />} backgroundColor="#34A853" />
        </Box>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
        <CourseSection title="My Courses" subtitle="Courses you have started." courses={data.in_progress}
          empty="You have not started any courses yet." />
        {suggested.length > 0 && (
          <CourseSection title="Suggested for You" subtitle="Courses available to your teams that you have not started."
            courses={suggested} />
        )}
        {data.completed.length > 0 && <CourseSection title="Completed Courses" courses={data.completed} />}
      </motion.div>
    </motion.div>
  );
}
